import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  IconButton,
  Divider,
  TextField,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { fetchCollectionMis, fetchCollectionSupervisors } from '../actions/collectionMisActions';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const now = new Date();
const CURRENT_MONTH = now.getMonth() + 1;
const CURRENT_YEAR = now.getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

const STATUS_OPTIONS = ['Paid', 'Pending', 'Hold'];

const STATUS_COLOR = {
  Paid: 'success',
  Pending: 'warning',
  Hold: 'error',
};

const fmt = (val) => (val == null ? '—' : val);
const fmtPct = (val) => (val == null ? '—' : `${val}%`);

const statusKey = (supervisorId, bank, product, identifier) =>
  `${supervisorId}__${bank}__${product}__${identifier}`;

const distAdvanceKey = (supervisorId, bank, product, memberName) =>
  `dist__${supervisorId}__${bank}__${product}__${memberName}`;

const BillStatusChip = ({ status }) => {
  if (!status) return <Typography variant="body2" color="text.secondary">—</Typography>;
  return (
    <Chip
      label={status}
      size="small"
      color={status === 'Raised' ? 'success' : 'warning'}
      variant="outlined"
    />
  );
};

const StatusCell = ({ value, isReadOnly, canOverridePaid, onChange }) => {
  const paidLocked = value === 'Paid' && !canOverridePaid;
  if (isReadOnly || paidLocked) {
    return value ? (
      <Chip label={value} size="small" color={STATUS_COLOR[value] || 'default'} variant="filled" />
    ) : (
      <Typography variant="body2" color="text.secondary">—</Typography>
    );
  }
  return (
    <Select
      value={value}
      size="small"
      displayEmpty
      onChange={(e) => onChange(e.target.value)}
      sx={{ minWidth: 110 }}
      renderValue={(val) =>
        val ? (
          <Chip label={val} size="small" color={STATUS_COLOR[val] || 'default'} variant="filled" sx={{ cursor: 'pointer' }} />
        ) : (
          <Typography variant="body2" color="text.secondary">—</Typography>
        )
      }
    >
      {STATUS_OPTIONS.map((opt) => (
        <MenuItem key={opt} value={opt}>
          <Chip label={opt} size="small" color={STATUS_COLOR[opt]} variant="filled" />
        </MenuItem>
      ))}
    </Select>
  );
};

const MemberTable = ({
  row,
  month,
  year,
  statusOverrides,
  isSubmitted,
  onSaveRow,
  isSaving,
  canOverridePaid,
}) => {
  const members = row.members || [];
  const distMembers = row.distributionMembers || [];

  const buildLocal = useCallback(() => {
    const salaryStatuses = {};
    const distStatuses = {};
    for (const m of members) {
      salaryStatuses[m.identifier] =
        statusOverrides[statusKey(row.supervisorId, row.bank, row.product, m.identifier)] ??
        m.status ?? '';
      distStatuses[m.name] =
        statusOverrides[statusKey(row.supervisorId, row.bank, row.product, `dist__${m.name}`)] ?? '';
    }
    return { salaryStatuses, distStatuses };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusOverrides, row]);

  const [local, setLocal] = useState(buildLocal);
  const [isEditing, setIsEditing] = useState(false);

  // Re-sync when not editing and parent data changes (after re-fetch or initial load)
  useEffect(() => {
    if (!isEditing) setLocal(buildLocal());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildLocal]);

  const isReadOnly = isSubmitted && !isEditing;

  const setSalaryStatus = (identifier, val) =>
    setLocal((prev) => ({ ...prev, salaryStatuses: { ...prev.salaryStatuses, [identifier]: val } }));

  const setDistStatus = (name, val) =>
    setLocal((prev) => ({ ...prev, distStatuses: { ...prev.distStatuses, [name]: val } }));

  const handleCancel = () => {
    setLocal(buildLocal());
    setIsEditing(false);
  };

  const handleSave = () => {
    onSaveRow(
      {
        supervisorId: row.supervisorId,
        bank: row.bank,
        product: row.product,
        members: members.map((m) => ({ identifier: m.identifier, name: m.name })),
        salaryStatuses: local.salaryStatuses,
        distStatuses: local.distStatuses,
      },
      (success) => { if (success) setIsEditing(false); },
    );
  };

  return (
    <Paper variant="outlined" sx={{ mb: 3 }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          backgroundColor: '#e3f2fd',
          borderBottom: '1px solid #90caf9',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Chip label={row.bank} size="small" color="primary" />
        <Typography variant="body2" color="text.secondary">—</Typography>
        <Chip label={row.product} size="small" color="secondary" variant="outlined" />
        {isSubmitted && (
          <Chip
            label={isEditing ? 'Editing' : 'Submitted'}
            color={isEditing ? 'warning' : 'success'}
            size="small"
            sx={{ ml: 'auto' }}
          />
        )}
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#fafafa' }}>
              <TableCell><strong>#</strong></TableCell>
              <TableCell><strong>Team Member</strong></TableCell>
              <TableCell><strong>Identifier</strong></TableCell>
              <TableCell align="center"><strong>Status</strong></TableCell>
              <TableCell><strong>Towards</strong></TableCell>
              <TableCell align="right"><strong>Amount After TDS</strong></TableCell>
              <TableCell align="right"><strong>Advance</strong></TableCell>
              <TableCell align="right"><strong>Payable Amount</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  No team members assigned
                </TableCell>
              </TableRow>
            ) : (
              members.map((m, idx) => {
                const dm = distMembers.find((d) => d.name.toLowerCase() === m.name.toLowerCase());
                const currentSalaryStatus = local.salaryStatuses[m.identifier] ?? '';
                const currentDistStatus = local.distStatuses[m.name] ?? '';

                const amtAfterTds = dm?.amountAfterTds ?? null;
                const dmAdvance = dm?.advance ?? null;
                const distPayable =
                  amtAfterTds != null && dmAdvance != null
                    ? parseFloat((amtAfterTds - dmAdvance).toFixed(2))
                    : amtAfterTds;

                const sharedCellSx = {
                  verticalAlign: 'middle',
                  borderBottom: '2px solid #e0e0e0',
                };

                return (
                  <React.Fragment key={m.identifier || m.name}>
                    {/* ── Salary row ── */}
                    <TableRow hover>
                      <TableCell rowSpan={2} sx={{ ...sharedCellSx, color: 'text.secondary' }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell rowSpan={2} sx={sharedCellSx}>
                        <Typography variant="body2" fontWeight="medium">{m.name}</Typography>
                      </TableCell>
                      <TableCell rowSpan={2} sx={sharedCellSx}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {m.identifier || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <StatusCell
                          value={currentSalaryStatus}
                          isReadOnly={isReadOnly}
                          canOverridePaid={canOverridePaid}
                          onChange={(val) => setSalaryStatus(m.identifier, val)}
                        />
                      </TableCell>
                      <TableCell>
                        {m.towards ? (
                          <Chip label={m.towards} size="small" variant="outlined" color="info" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{fmt(m.amountAfterTds)}</TableCell>
                      <TableCell align="right">{fmt(m.advance)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">{fmt(m.payableAmount)}</Typography>
                      </TableCell>
                    </TableRow>

                    {/* ── Payout row ── */}
                    <TableRow>
                      <TableCell align="center" sx={{ borderBottom: '2px solid #e0e0e0' }}>
                        <StatusCell
                          value={currentDistStatus}
                          isReadOnly={isReadOnly}
                          canOverridePaid={canOverridePaid}
                          onChange={(val) => setDistStatus(m.name, val)}
                        />
                      </TableCell>
                      <TableCell sx={{ borderBottom: '2px solid #e0e0e0' }}>
                        <Chip label="Payout" size="small" variant="outlined" color="success" />
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: '2px solid #e0e0e0' }}>
                        {amtAfterTds != null ? amtAfterTds : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: '2px solid #e0e0e0' }}>
                        <Typography variant="body2">
                          {dmAdvance != null ? dmAdvance : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: '2px solid #e0e0e0' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {distPayable != null ? distPayable : '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1.5, borderTop: '1px solid #e0e0e0' }}>
        {!isSubmitted && (
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={14} /> : null}
          >
            {isSaving ? 'Submitting…' : 'Submit'}
          </Button>
        )}
        {isSubmitted && !isEditing && (
          <Button variant="outlined" size="small" onClick={() => setIsEditing(true)}>
            Edit
          </Button>
        )}
        {isSubmitted && isEditing && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={14} /> : null}
            >
              {isSaving ? 'Updating…' : 'Update'}
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const penaltyKey = (bank, product) => `${bank}||${product}`;

const PayoutSummaryTable = ({ rows, penalties, onSavePenalties, isSaving }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localPenalties, setLocalPenalties] = useState({});

  const buildLocalPenalties = useCallback(() => {
    const init = {};
    for (const row of rows) {
      init[penaltyKey(row.bank, row.product)] = penalties[penaltyKey(row.bank, row.product)] ?? 0;
    }
    return init;
  }, [rows, penalties]);

  useEffect(() => {
    if (!isEditing) setLocalPenalties(buildLocalPenalties());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildLocalPenalties]);

  const handleCancel = () => {
    setLocalPenalties(buildLocalPenalties());
    setIsEditing(false);
  };

  const handleSave = () => {
    onSavePenalties(localPenalties, (success) => {
      if (success) setIsEditing(false);
    });
  };

  return (
    <Paper variant="outlined" sx={{ mb: 3 }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          backgroundColor: '#f3e5f5',
          borderBottom: '1px solid #ce93d8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold">Payout Summary</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isEditing && (
            <Button variant="outlined" size="small" onClick={() => setIsEditing(true)}>Edit</Button>
          )}
          {isEditing && (
            <>
              <Button variant="outlined" size="small" onClick={handleCancel} disabled={isSaving}>Cancel</Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={isSaving}
                startIcon={isSaving ? <CircularProgress size={14} /> : null}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#fafafa' }}>
              <TableCell><strong>#</strong></TableCell>
              <TableCell><strong>Bank</strong></TableCell>
              <TableCell><strong>Product</strong></TableCell>
              <TableCell align="right"><strong>Bill After TDS</strong></TableCell>
              <TableCell align="right"><strong>Dist %</strong></TableCell>
              <TableCell align="right"><strong>Advances</strong></TableCell>
              <TableCell align="right"><strong>Penalty</strong></TableCell>
              <TableCell align="right"><strong>Salary Payable</strong></TableCell>
              <TableCell align="right"><strong>Payout Payable</strong></TableCell>
              <TableCell align="right"><strong>Payout</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => {
              const pk = penaltyKey(row.bank, row.product);
              const penaltyVal = parseFloat(localPenalties[pk] ?? 0) || 0;
              const advances = parseFloat(
                (row.distributionMembers || [])
                  .reduce((acc, dm) => acc + (dm.advance ?? 0), 0)
                  .toFixed(2),
              );
              const distPct = row.distributionPercent ?? null;
              const billAfterTds = row.billAmountAfterTDS ?? null;
              const payout =
                billAfterTds != null && distPct != null
                  ? parseFloat(
                    (
                      (billAfterTds * distPct) / 100 -
                      advances -
                      penaltyVal -
                      row.fixed +
                      row.payouts
                    ).toFixed(2),
                  )
                  : null;

              return (
                <TableRow key={pk} hover>
                  <TableCell sx={{ color: 'text.secondary' }}>{idx + 1}</TableCell>
                  <TableCell><Chip label={row.bank} size="small" variant="outlined" /></TableCell>
                  <TableCell>
                    <Chip label={row.product} size="small" variant="outlined" color="primary" />
                  </TableCell>
                  <TableCell align="right">{fmt(billAfterTds)}</TableCell>
                  <TableCell align="right">{distPct != null ? `${distPct}%` : '—'}</TableCell>
                  <TableCell align="right">{advances}</TableCell>
                  <TableCell align="right">
                    {isEditing ? (
                      <TextField
                        size="small"
                        type="number"
                        value={localPenalties[pk] ?? 0}
                        onChange={(e) =>
                          setLocalPenalties((prev) => ({ ...prev, [pk]: e.target.value }))
                        }
                        inputProps={{ style: { textAlign: 'right' }, min: 0 }}
                        sx={{ width: 110 }}
                      />
                    ) : (
                      <Typography variant="body2">{penaltyVal}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{fmt(row.fixed)}</TableCell>
                  <TableCell align="right">{fmt(row.payouts)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {payout != null ? payout : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const CollectionMis = () => {
  const dispatch = useDispatch();

  const [month, setMonth] = useState(CURRENT_MONTH);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [supervisorId, setSupervisorId] = useState('');
  const [statusOverrides, setStatusOverrides] = useState({});
  const [submittedRows, setSubmittedRows] = useState(new Set());
  const [savingRows, setSavingRows] = useState(new Set());
  const [penalties, setPenalties] = useState({});
  const [savingPenalties, setSavingPenalties] = useState(false);

  const { loading, rows = [], error } = useSelector((s) => s.collectionMis);
  const { supervisors = [] } = useSelector((s) => s.collectionSupervisors);
  const { userInfo } = useSelector((s) => s.userLogin);
  const canOverridePaid = !!userInfo?.role?.collectionMisOverridePaid;

  const loadData = useCallback(() => {
    dispatch(fetchCollectionMis(month, year));
  }, [dispatch, month, year]);

  const fetchStatuses = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/collection-mis/member-statuses?month=${month}&year=${year}`,
        { headers: { Authorization: `Bearer ${userInfo.token}` } },
      );
      const map = {};
      const submitted = new Set();
      for (const s of data) {
        map[statusKey(s.supervisorId, s.bank, s.product, s.memberIdentifier)] = s.status;
        submitted.add(`${s.supervisorId}-${s.bank}-${s.product}`);
      }
      setStatusOverrides(map);
      setSubmittedRows(submitted);
    } catch (_) {
      // non-critical
    }
  }, [month, year, userInfo]);

  const fetchPenalties = useCallback(async () => {
    if (!supervisorId) { setPenalties({}); return; }
    try {
      const { data } = await axios.get(
        `${API_URL}/collection-mis/penalties?month=${month}&year=${year}&supervisorId=${supervisorId}`,
        { headers: { Authorization: `Bearer ${userInfo.token}` } },
      );
      const map = {};
      for (const p of data) {
        map[penaltyKey(p.bank, p.product)] = p.penalty;
      }
      setPenalties(map);
    } catch (_) { }
  }, [month, year, supervisorId, userInfo]);

  const handleSavePenalties = useCallback(
    async (localPenalties, onDone) => {
      setSavingPenalties(true);
      try {
        const headers = { Authorization: `Bearer ${userInfo.token}` };
        await Promise.all(
          Object.entries(localPenalties).map(([key, penalty]) => {
            const sepIdx = key.indexOf('||');
            const bank = key.slice(0, sepIdx);
            const product = key.slice(sepIdx + 2);
            return axios.patch(
              `${API_URL}/collection-mis/penalty`,
              { supervisorId, month, year, bank, product, penalty: parseFloat(penalty) || 0 },
              { headers },
            );
          }),
        );
        const updated = {};
        for (const [key, penalty] of Object.entries(localPenalties)) {
          updated[key] = parseFloat(penalty) || 0;
        }
        setPenalties(updated);
        toast.success('Penalties saved');
        onDone(true);
      } catch (_) {
        toast.error('Failed to save penalties');
        onDone(false);
      } finally {
        setSavingPenalties(false);
      }
    },
    [supervisorId, month, year, userInfo],
  );

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  useEffect(() => {
    fetchPenalties();
  }, [fetchPenalties]);

  useEffect(() => {
    dispatch(fetchCollectionSupervisors());
  }, [dispatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveRow = useCallback(
    async ({ supervisorId: supId, bank, product, members, salaryStatuses, distStatuses }, onDone) => {
      const rowKey = `${supId}-${bank}-${product}`;
      setSavingRows((prev) => new Set([...prev, rowKey]));
      try {
        const headers = { Authorization: `Bearer ${userInfo.token}` };
        await Promise.all([
          // salary statuses (only non-empty)
          ...members
            .filter((m) => salaryStatuses[m.identifier])
            .map((m) =>
              axios.patch(
                `${API_URL}/collection-mis/member-status`,
                { supervisorId: supId, month, year, bank, product, memberIdentifier: m.identifier, status: salaryStatuses[m.identifier] },
                { headers },
              ),
            ),
          // dist statuses (only non-empty)
          ...members
            .filter((m) => distStatuses[m.name])
            .map((m) =>
              axios.patch(
                `${API_URL}/collection-mis/member-status`,
                { supervisorId: supId, month, year, bank, product, memberIdentifier: `dist__${m.name}`, status: distStatuses[m.name] },
                { headers },
              ),
            ),
        ]);

        // Update saved state so MemberTable re-syncs
        setStatusOverrides((prev) => {
          const next = { ...prev };
          for (const m of members) {
            if (salaryStatuses[m.identifier])
              next[statusKey(supId, bank, product, m.identifier)] = salaryStatuses[m.identifier];
            if (distStatuses[m.name])
              next[statusKey(supId, bank, product, `dist__${m.name}`)] = distStatuses[m.name];
          }
          return next;
        });
        setSubmittedRows((prev) => new Set([...prev, rowKey]));

        toast.success('Saved successfully');
        onDone(true);
      } catch (_) {
        toast.error('Failed to save. Please try again.');
        onDone(false);
      } finally {
        setSavingRows((prev) => {
          const next = new Set(prev);
          next.delete(rowKey);
          return next;
        });
      }
    },
    [month, year, userInfo],
  );

  const filteredRows = useMemo(
    () => (supervisorId ? rows.filter((r) => r.supervisorId === supervisorId) : rows),
    [rows, supervisorId],
  );

  const bankGroups = useMemo(() => {
    const groups = [];
    const seen = {};
    for (const row of filteredRows) {
      const key = `${row.supervisorId}__${row.bank}`;
      if (!seen[key]) {
        seen[key] = { bank: row.bank, rows: [] };
        groups.push(seen[key]);
      }
      seen[key].rows.push(row);
    }
    return groups;
  }, [filteredRows]);

  const totalRows = bankGroups.reduce((acc, g) => acc + g.rows.length, 0);

  const selectedSupervisorName = supervisorId
    ? (supervisors.find((s) => s._id === supervisorId)?.name || '')
    : '';

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          Collection MIS
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadData}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
                {MONTH_NAMES.map((m, i) => (
                  <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
                {YEAR_OPTIONS.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Supervisor</InputLabel>
              <Select
                value={supervisorId}
                label="Supervisor"
                onChange={(e) => setSupervisorId(e.target.value)}
              >
                <MenuItem value="">All Supervisors</MenuItem>
                {supervisors.map((s) => (
                  <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* MIS Summary Table */}
      <Paper sx={{ mb: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>Bank</strong></TableCell>
                  <TableCell><strong>Product</strong></TableCell>
                  <TableCell align="right"><strong>Bill Amt</strong></TableCell>
                  <TableCell align="right"><strong>Bill After TDS</strong></TableCell>
                  <TableCell align="right"><strong>Fixed</strong></TableCell>
                  <TableCell align="right"><strong>Payout</strong></TableCell>
                  <TableCell align="right"><strong>Total Payable</strong></TableCell>
                  <TableCell align="right"><strong>Fixed %</strong></TableCell>
                  <TableCell align="right"><strong>Payout %</strong></TableCell>
                  <TableCell align="right"><strong>Total Payable %</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {totalRows === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No data found for {MONTH_NAMES[month - 1]} {year}
                      {selectedSupervisorName ? ` — ${selectedSupervisorName}` : ''}
                    </TableCell>
                  </TableRow>
                ) : (
                  bankGroups.flatMap((group, bankIdx) =>
                    group.rows.map((row, rowIdx) => (
                      <TableRow
                        key={`${row.supervisorId}-${row.bank}-${row.product}`}
                        hover
                        sx={{
                          '& td': {
                            borderBottom:
                              rowIdx === group.rows.length - 1
                                ? '2px solid #e0e0e0'
                                : undefined,
                          },
                        }}
                      >
                        {rowIdx === 0 && (
                          <>
                            <TableCell
                              rowSpan={group.rows.length}
                              align="center"
                              sx={{ fontWeight: 'bold', color: 'text.secondary', verticalAlign: 'middle' }}
                            >
                              {bankIdx + 1}
                            </TableCell>
                            <TableCell
                              rowSpan={group.rows.length}
                              sx={{ verticalAlign: 'middle' }}
                            >
                              <Chip label={group.bank} size="small" variant="outlined" />
                            </TableCell>
                          </>
                        )}
                        <TableCell>
                          <Chip label={row.product} size="small" variant="outlined" color="primary" />
                        </TableCell>
                        <TableCell align="right">{fmt(row.billRaised)}</TableCell>
                        <TableCell align="right">{fmt(row.billAmountAfterTDS)}</TableCell>
                        <TableCell align="right">{fmt(row.fixed)}</TableCell>
                        <TableCell align="right">{fmt(row.payouts)}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {fmt(row.totalPayable)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{fmtPct(row.pctFixed)}</TableCell>
                        <TableCell align="right">{fmtPct(row.pctPayout)}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {fmtPct(row.totalPayablePct)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <BillStatusChip status={row.status} />
                        </TableCell>
                      </TableRow>
                    ))
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Team Member Details */}
      {!loading && !error && supervisorId && filteredRows.length > 0 && (
        <Box>
          <Divider sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {selectedSupervisorName}'s Payout Details
            </Typography>
          </Divider>

          <PayoutSummaryTable
            rows={filteredRows}
            penalties={penalties}
            onSavePenalties={handleSavePenalties}
            isSaving={savingPenalties}
          />

          <Divider sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Team Member's Details
            </Typography>
          </Divider>

          {filteredRows.map((row) => {
            const rowKey = `${row.supervisorId}-${row.bank}-${row.product}`;
            return (
              <MemberTable
                key={rowKey}
                row={row}
                month={month}
                year={year}
                statusOverrides={statusOverrides}
                isSubmitted={submittedRows.has(rowKey)}
                onSaveRow={handleSaveRow}
                isSaving={savingRows.has(rowKey)}
                canOverridePaid={canOverridePaid}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default CollectionMis;
